import { ArrowDown } from '@sds-system/assets';
import { useTheme } from '@sds-system/system';
import type { PolymorphicRef } from '@sds-system/types';
import { useForkRef } from '@sds-system/utils';
import { type ElementType, forwardRef, useEffect, useRef } from 'react';
import { Column } from '../Column';
import { Icon } from '../Icon';
import { Row } from '../Row';
import { selectFontStyle, selectGhostSizeStyle } from '../Select/Select.styled';
import { Typography } from '../Typography';
import { StyledSystemSelectTrigger } from './SystemSelect.styled';
import type { SystemSelectComponent, SystemSelectProps } from './SystemSelect.types';

export const SystemSelect: SystemSelectComponent = forwardRef(
  <C extends ElementType = 'select'>(props: SystemSelectProps<C>, ref: PolymorphicRef<C>) => {
    const {
      variant = 'outline',
      options,
      size = 'M',
      disabled,
      value,
      defaultValue,
      placeholder,
      onValueChange,
      isError,
      errorMessages,
      styledComponents,
      isAutoWidth,
      iconRight,
      ...rest
    } = props;

    const selectRef = useRef<HTMLSelectElement>(null);
    const forkRef = useForkRef(ref, selectRef);
    const theme = useTheme();

    useEffect(() => {
      if (isAutoWidth) {
        const adjustWidth = () => {
          if (selectRef.current) {
            const option = selectRef.current.querySelector('option:checked');
            const tempSpan = document.createElement('span') as HTMLOptionElement;

            tempSpan.style.visibility = 'hidden';
            tempSpan.style.whiteSpace = 'nowrap';
            tempSpan.style.fontSize = selectFontStyle(theme, size, variant).fontSize as string;
            tempSpan.style.letterSpacing = selectFontStyle(theme, size, variant)
              .letterSpacing as string;
            tempSpan.textContent = option?.textContent || '';

            document.body.appendChild(tempSpan);

            const width =
              tempSpan.offsetWidth + selectGhostSizeStyle(size).paddingLeft * 2 + 16 + 4;

            selectRef.current.style.minWidth = width + 'px';

            selectRef.current.style.width = width + 'px';

            document.body.removeChild(tempSpan);
          }
        };

        adjustWidth();

        selectRef.current?.addEventListener('change', adjustWidth);

        return () => selectRef.current?.removeEventListener('change', adjustWidth);
      }

      return () => {};
    }, []);

    return (
      <Column width={isAutoWidth ? 'max-content' : 'auto'}>
        <Row alignItems='center' position='relative' width={isAutoWidth ? 'max-content' : 'auto'}>
          <StyledSystemSelectTrigger
            defaultValue={defaultValue}
            disabled={disabled}
            isError={isError}
            onChange={(e) => onValueChange && onValueChange(e.target.value)}
            ref={forkRef}
            triggerSize={size}
            value={value}
            variant={variant}
            {...(styledComponents?.trigger && styledComponents.trigger)}
            {...rest}
          >
            {placeholder && (
              <option disabled hidden value={0}>
                {placeholder}
              </option>
            )}
            {options.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </StyledSystemSelectTrigger>
          {variant === 'ghost' && (
            <Icon
              size='3XS'
              source={ArrowDown}
              style={{
                position: 'absolute',
                right: iconRight || selectGhostSizeStyle(size).paddingRight,
                pointerEvents: 'none',
              }}
            />
          )}
          {variant === 'outline' && (
            <Icon
              size='2XS'
              source={ArrowDown}
              style={{ position: 'absolute', right: 16, pointerEvents: 'none' }}
            />
          )}
        </Row>
        {isError &&
          errorMessages?.map((message) => (
            <Column key={message}>
              <Typography color='text.error' fontType='caption.medium'>
                {message}
              </Typography>
            </Column>
          ))}
      </Column>
    );
  },
);
